import {
  CheckOutlined,
  CloseOutlined,
  PaperClipOutlined,
  PlusOutlined
} from "@ant-design/icons"
import {
  App,
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Select,
  Tag,
  Tooltip
} from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { rowCommitKeyDown } from "../../../../../../../components/EntityTable/editable-row-cells.ts"
import { MODAL_CONFIRM_CONFIG } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { FormChangedWatcher } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import InputNumber from "../../../../../../../components/Input/InputNumber.tsx"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import { DATE_FORMAT } from "../../../../../../../utils/date/date.ts"
import { dateAutoFill } from "../../../../../../../utils/form/date-auto-fill.tsx"
import { validateFormWithScroll } from "../../../../../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../../../../../utils/object/is-form-changed.ts"
import { pickableOptions } from "../../../../../../../utils/select/managed-list-options.ts"
import type { FinancialTransactionItem } from "../../../../../types/project-finance.types.tsx"
import type { ProjectInvoice } from "../../../../../types/project-invoices.types.tsx"
import {
  ENTRY_TYPE_LABELS,
  ENTRY_TYPE_OPTIONS,
  ENTRY_TYPES,
  FINANCE_ITEM_STATUSES,
  FINANCE_ITEM_STATUSES_MAP,
  FINANCE_ITEM_TYPES
} from "../../../../constants/finance-item-constants.ts"
import { ProjectViewFinanceCellDueDate } from "../../../ProjectViewFinanceCellDueDate.tsx"
import { ProjectViewFinanceNewRowMilestones } from "./ProjectViewFinanceNewRowMilestones.tsx"
import { ProjectViewFinancePersonSelect } from "./ProjectViewFinancePersonSelect.tsx"
import { QuickAddInvoiceDrawer } from "./QuickAddInvoiceDrawer.tsx"
import {
  buildItemTypeOptions,
  buildResponsibleTypeOptions,
  isExpenseAllowed,
  isPayableAllowed,
  isReceivableAllowed,
  PAYABLE_DISABLED_REASON,
  RECEIVABLE_DISABLED_REASON,
  responsibleTypeDisabledReason
} from "./responsible-type-options.ts"
import { statusOnCreate } from "./status-on-create.ts"
import { buildStatusOptions } from "./status-options.ts"
import { RESPONSIBLE_TYPES } from "../../../../constants/responsible-type-constants.ts"
import { ResolvedSelect } from "../../../../../../../components/Select/ResolvedSelect.tsx"

interface ProjectViewFinanceNewRowProps {
  record: FinancialTransactionItem
  columns: {
    key?: React.Key
    width?: number | string
    fixed?: boolean | "left" | "right"
  }[]
  options: Record<string, BaseOptionType[]>
  mode?: "create" | "edit"
  onSelectRow: (
    record: FinancialTransactionItem,
    action: string
  ) => Promise<void> | void
  invoiceCreateAllowed?: boolean
  invoiceSelfAssigned?: boolean
  userId?: string
}

export const ProjectViewFinanceNewRow = ({
  record,
  columns,
  onSelectRow,
  options,
  mode = "create",
  invoiceCreateAllowed,
  invoiceSelfAssigned,
  userId,
  ...rest
}: ProjectViewFinanceNewRowProps) => {
  const { getDateValueProps, normalizeDate, userFormat } = useDateFormat()
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<FinancialTransactionItem>()
  const { modal } = App.useApp()
  const { t } = useTranslation()

  const [invoiceDrawerOpen, setInvoiceDrawerOpen] = useState(false)
  const [extraInvoiceOptions, setExtraInvoiceOptions] = useState<
    BaseOptionType[]
  >([])

  const isCreate = mode === "create"
  const projectTypesOptions = options?.["project_type"]

  const itemType = Form.useWatch("item_type", form)
  const responsibleType = Form.useWatch("responsible_type", form)
  const expenseAllowed = isExpenseAllowed(itemType, record?.is_planned)
  const createStatus = statusOnCreate(responsibleType, record?.is_planned)
  const receivableAllowed = isReceivableAllowed(responsibleType)
  const payableAllowed = isPayableAllowed(responsibleType)
  const responsibleTypeOptions = useMemo(
    () =>
      buildResponsibleTypeOptions(
        options?.["responsible_type"],
        expenseAllowed,
        itemType
      ),
    [options, expenseAllowed, itemType]
  )
  const itemTypeOptions = useMemo(
    () =>
      buildItemTypeOptions(
        options?.["item_type"],
        receivableAllowed,
        payableAllowed
      ),
    [options, receivableAllowed, payableAllowed]
  )
  const statusOptions = useMemo(
    () => buildStatusOptions(options?.["status"], record?.partial_finances),
    [options, record?.partial_finances]
  )

  const entryType = Form.useWatch("entry_type", form)
  // Keep a legacy entry_type (e.g. invoice/refund on an existing row) selectable
  // with its proper label, even though the picker is reduced to the active types.
  const entryTypeOptions = useMemo(() => {
    const base = options?.["entry_type"]?.length
      ? options["entry_type"]
      : ENTRY_TYPE_OPTIONS
    if (entryType && !base.some((option) => option.value === entryType)) {
      return [
        { value: entryType, label: ENTRY_TYPE_LABELS[entryType] ?? entryType },
        ...base
      ]
    }
    return base
  }, [options, entryType])

  // EXPENSE only saves on an actual payable line; if the line turns receivable
  // or planned, drop the now-invalid Source/Target instead of letting the user
  // submit into a server rejection.
  useEffect(() => {
    if (responsibleType === RESPONSIBLE_TYPES.EXPENSE && !expenseAllowed) {
      form.setFieldValue("responsible_type", null)
    }
  }, [expenseAllowed, responsibleType, form])

  // A Receivable is customer-only; if the contact becomes a non-customer, drop
  // the now-invalid type so it can't be saved against a non-customer.
  useEffect(() => {
    if (itemType === FINANCE_ITEM_TYPES.RECEIVABLE && !receivableAllowed) {
      form.setFieldValue("item_type", null)
    }
  }, [receivableAllowed, itemType, form])

  // The mirror: a customer can't be a payable; if the contact becomes a
  // customer, drop the now-invalid type.
  useEffect(() => {
    if (itemType === FINANCE_ITEM_TYPES.PAYABLE && !payableAllowed) {
      form.setFieldValue("item_type", null)
    }
  }, [payableAllowed, itemType, form])

  const projectTypeMilestonesMap = useMemo((): Map<
    string,
    BaseOptionType[]
  > => {
    return new Map(
      projectTypesOptions?.map((item) => [item?.value, item?.milestones]) ?? []
    )
  }, [projectTypesOptions])

  const handleSave = async () => {
    let validated: FinancialTransactionItem
    try {
      validated = await validateFormWithScroll(form)
    } catch {
      return
    }

    const {
      id,
      partial_finances: _partialFinances,
      planned_value: _plannedValue,
      ...initialData
    } = record
    const { date, due_date, ...values } = validated

    const action = isCreate ? "create" : "update"

    setSaving(true)

    try {
      await onSelectRow(
        {
          ...initialData,
          ...values,
          subcontractor: values?.subcontractor || null,
          assignee: values?.assignee || null,
          custom_subcontractor_name: values?.custom_subcontractor_name || null,
          date: normalizeDate(date),
          due_date: normalizeDate(due_date)
        },
        action
      )
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const action = isCreate ? "cancel_create" : "cancel"

    const isChanged = isFormChanged<FinancialTransactionItem>(
      form.getFieldsValue(),
      record
    )

    if (isChanged) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk() {
          onSelectRow(record, action)
        }
      })
    } else {
      onSelectRow(record, action)
    }
  }

  const handleInvoiceCreated = (invoice: ProjectInvoice) => {
    setExtraInvoiceOptions((prev) => [
      { value: invoice.id, label: invoice.invoice_number },
      ...prev.filter((option) => option.value !== invoice.id)
    ])
    form.setFieldValue("invoice", invoice.id)
    setInvoiceDrawerOpen(false)
  }

  const rowClassName = isCreate ? "bg-blue-50" : ""

  const fixedRightOffsets = useMemo(() => {
    const offsets: Record<string, number> = {}
    let offset = 0
    for (let i = columns.length - 1; i >= 0; i--) {
      const column = columns[i]
      if (column.fixed === "right") {
        offsets[String(column.key)] = offset
        offset += Number(column.width) || 0
      }
    }
    return offsets
  }, [columns])

  const firstFixedRightKey = useMemo(
    () => columns.find((column) => column.fixed === "right")?.key,
    [columns]
  )

  const fixedRightCellProps = (key: string, extraClassName = "") => {
    if (!(key in fixedRightOffsets)) {
      return {
        className: ["ant-table-cell", extraClassName, rowClassName]
          .filter(Boolean)
          .join(" ")
      }
    }

    return {
      className: [
        "sticky ant-table-cell ant-table-cell-fix-right",
        String(key) === String(firstFixedRightKey) &&
          "ant-table-cell-fix-right-first",
        extraClassName,
        rowClassName
      ]
        .filter(Boolean)
        .join(" "),
      style: { right: fixedRightOffsets[key] }
    }
  }

  const cellByKey: Record<string, React.ReactNode> = {
    date: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="date"
          getValueProps={getDateValueProps}
          noStyle
          rules={[{ required: true, message: "Required" }]}
        >
          <DatePicker
            className="w-full"
            format={userFormat ?? DATE_FORMAT}
            onChange={dateAutoFill<FinancialTransactionItem>({
              form,
              endDatePath: ["due_date"]
            })}
          />
        </Form.Item>
      </td>
    ),
    name: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="name"
          noStyle
          rules={[
            { required: true, message: "Enter name" },
            {
              max: 255,
              message: "Must be at least 255 characters"
            }
          ]}
        >
          <Input.TextArea
            autoSize
            placeholder="Enter name"
            autoFocus={isCreate}
          />
        </Form.Item>
      </td>
    ),
    item_type_display: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="item_type"
          noStyle
          rules={[{ required: true, message: "Required" }]}
        >
          <Select
            className="w-full"
            placeholder="Select type"
            options={itemTypeOptions}
            optionRender={(option) =>
              option.data?.disabled ? (
                <Tooltip
                  title={
                    option.value === FINANCE_ITEM_TYPES.PAYABLE
                      ? PAYABLE_DISABLED_REASON
                      : RECEIVABLE_DISABLED_REASON
                  }
                  placement="left"
                >
                  <span className="block">{option.label}</span>
                </Tooltip>
              ) : (
                option.label
              )
            }
          />
        </Form.Item>
      </td>
    ),
    entry_type: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="entry_type"
          noStyle
          rules={[{ required: true, message: "Required" }]}
        >
          <Select
            className="w-full"
            placeholder="Select entry type"
            options={entryTypeOptions}
          />
        </Form.Item>
      </td>
    ),
    due_date: (
      <ProjectViewFinanceCellDueDate<FinancialTransactionItem> form={form} />
    ),
    comment: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="comment"
          noStyle
          rules={[
            {
              whitespace: true,
              max: 250,
              message: "Must be at least 250 characters"
            }
          ]}
        >
          <Input.TextArea rows={2} autoSize placeholder="Enter comment" />
        </Form.Item>
      </td>
    ),
    value: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="value"
          noStyle
          rules={[{ required: false, message: "Required" }]}
        >
          <InputNumber
            min={0}
            max={999999999.99}
            precision={2}
            className="w-full"
            prefix="$"
            placeholder="0.00"
          />
        </Form.Item>
      </td>
    ),
    responsible_type_display: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="responsible_type"
          noStyle
          normalize={(v) => v || null}
        >
          <Select
            allowClear
            className="w-full"
            placeholder="Select type"
            options={responsibleTypeOptions}
            optionRender={(option) =>
              option.data?.disabled ? (
                <Tooltip
                  title={responsibleTypeDisabledReason(
                    Number(option.value),
                    itemType,
                    expenseAllowed
                  )}
                  placement="left"
                >
                  <span className="block">{option.label}</span>
                </Tooltip>
              ) : (
                option.label
              )
            }
          />
        </Form.Item>
      </td>
    ),
    custom_subcontractor_name: (
      <td className="ant-table-cell">
        <ProjectViewFinancePersonSelect
          form={form}
          subcontractors={options?.["subcontractors"]}
          projectSubcontractors={options?.["project_subcontractors"]}
          teamMembers={options?.["team_members"]}
          suppliers={options?.["suppliers"]}
          hasInitialValue={Boolean(
            record?.custom_subcontractor_name ||
              record?.subcontractor ||
              record?.assignee
          )}
        />
      </td>
    ),
    project_type_display: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="project_type"
          noStyle
          normalize={(value: string) => value || null}
          getValueFromEvent={(
            value: FinancialTransactionItem["project_type"]
          ) => {
            form.setFieldValue("project_phase", null)
            return value
          }}
        >
          <Select
            className="w-full"
            placeholder="Select type"
            options={projectTypesOptions}
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input?.toLowerCase())
            }
          />
        </Form.Item>
      </td>
    ),
    project_phase_name: (
      <td className="ant-table-cell">
        <ProjectViewFinanceNewRowMilestones
          form={form}
          options={options}
          projectTypeMilestonesMap={projectTypeMilestonesMap}
          currentMilestoneId={record?.project_phase}
          currentMilestoneName={record?.project_phase_name}
        />
      </td>
    ),
    invoice_display: (
      <td className="ant-table-cell">
        <Flex gap={4} align="center" className="w-full">
          <div className="min-w-0 flex-1">
            <Form.Item<FinancialTransactionItem>
              name="invoice"
              noStyle
              normalize={(value: string) => value || null}
            >
              <Select
                className="w-full"
                placeholder="Select invoice"
                options={[
                  ...(options?.["invoices"] ?? []),
                  ...extraInvoiceOptions
                ]}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input?.toLowerCase())
                }
              />
            </Form.Item>
          </div>
          {invoiceCreateAllowed && (
            <Tooltip title={t("finance.quickAddInvoice.tooltip")}>
              <Button
                icon={<PlusOutlined />}
                aria-label={t("finance.quickAddInvoice.tooltip")}
                onClick={() => setInvoiceDrawerOpen(true)}
              />
            </Tooltip>
          )}
        </Flex>
        {invoiceCreateAllowed && (
          <QuickAddInvoiceDrawer
            open={invoiceDrawerOpen}
            onClose={() => setInvoiceDrawerOpen(false)}
            onCreated={handleInvoiceCreated}
            selfAssigned={invoiceSelfAssigned}
            userId={userId}
          />
        )}
      </td>
    ),
    payment_method_display: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="payment_method"
          noStyle
          normalize={(v) => v || null}
        >
          <ResolvedSelect
            allowClear
            className={"w-full"}
            placeholder="Select method"
            options={pickableOptions(options?.["payment_method"])}
          />
        </Form.Item>
      </td>
    ),
    payment_details: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="payment_details"
          noStyle
          rules={[
            {
              max: 100,
              message: "Must be at least 100 characters"
            }
          ]}
        >
          <Input.TextArea autoSize placeholder="Enter details" />
        </Form.Item>
      </td>
    ),
    payments: <td {...fixedRightCellProps("payments")} />,
    status: (
      <td {...fixedRightCellProps("status")}>
        {isCreate ? (
          // The backend assigns the status when none is sent. There is nothing
          // to pick, so we show a read-only badge of what it will assign and
          // omit `status` from the payload.
          <Tag
            style={FINANCE_ITEM_STATUSES_MAP[createStatus].style}
            className="m-0"
          >
            {FINANCE_ITEM_STATUSES_MAP[createStatus].label}
          </Tag>
        ) : (
          <Form.Item<FinancialTransactionItem>
            name="status"
            noStyle
            rules={[{ required: true, message: "Required" }]}
          >
            <Select
              allowClear
              className="w-full"
              placeholder="Select status"
              options={statusOptions}
            />
          </Form.Item>
        )}
      </td>
    ),
    attachments: (
      <td {...fixedRightCellProps("attachments", "text-center")}>
        <Tooltip title="Only available in view mode">
          <Button disabled icon={<PaperClipOutlined />} />
        </Tooltip>
      </td>
    ),
    actions: (
      <td {...fixedRightCellProps("actions")}>
        <Flex gap="small" justify="center">
          <Button
            variant="outlined"
            color="danger"
            onClick={handleCancel}
            icon={<CloseOutlined />}
          />
          <Button
            loading={saving}
            variant="outlined"
            color="green"
            onClick={handleSave}
            icon={isCreate ? <PlusOutlined /> : <CheckOutlined />}
          />
        </Flex>
      </td>
    )
  }

  const onKeyDownRow = rowCommitKeyDown(() => void handleSave(), {
    busy: saving
  })

  return (
    <Form<FinancialTransactionItem>
      form={form}
      component={false}
      initialValues={{
        ...record,
        entry_type: record?.entry_type ?? ENTRY_TYPES.PAYMENT
      }}
      disabled={saving}
    >
      <FormChangedWatcher<FinancialTransactionItem> form={form} saving={saving} />

      <tr {...rest} onKeyDown={onKeyDownRow} className={rowClassName}>
        {columns.map((column) => (
          <React.Fragment key={String(column.key)}>
            {cellByKey[String(column.key)] ?? (
              <td className="ant-table-cell" />
            )}
          </React.Fragment>
        ))}
      </tr>
    </Form>
  )
}
